import type { EstimatorHandoff, OutcomeEnvelope } from '../../api/lib/estimator/contracts/handoff';
import type { CostBasis } from '../../api/lib/estimator/outcomes';
import { upload } from '@vercel/blob/client';
import type { DealSummary, DealDetailView } from '../types/deal';
import type { DevHealthResponse } from '../types/dev-health';
import type {
  CreateSiteScoutShareInput,
  JobTree,
  SiteScoutJobSummary,
  SiteScoutLinkedProject,
  SiteScoutShare,
  SiteScoutShareCreated,
} from '../types/site-scout';

/** Authenticated user shape returned by /auth/login and /auth/me. */
export interface AuthUser {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  role: 'admin' | 'manager' | 'employee';
  permissions: Record<string, boolean>;
}

export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Global API error reporting. ToastProvider registers a handler on mount so
 * every failed request surfaces as a toast without per-page wiring. No-op
 * until registered (e.g. in tests).
 */
type ApiErrorHandler = (message: string) => void;

let apiErrorHandler: ApiErrorHandler | null = null;

export function registerApiErrorHandler(handler: ApiErrorHandler | null): void {
  apiErrorHandler = handler;
}

export function reportApiError(message: string): void {
  apiErrorHandler?.(message);
}

interface RequestOptions extends RequestInit {
  /** Suppress the global error toast for intentionally-handled failures. */
  silent?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { silent, ...fetchOptions } = options;
  try {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(fetchOptions.headers || {}) },
      ...fetchOptions,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Request failed: ${res.status}`);
    }
    return res.json();
  } catch (error) {
    if (!silent) {
      reportApiError(error instanceof Error ? error.message : 'Request failed');
    }
    throw error;
  }
}

/** Images above this size are compressed before upload or OCR. */
const UPLOAD_IMAGE_COMPRESS_THRESHOLD_MB = 4;

/**
 * Shared pre-upload step: large images (e.g. phone photos/scans) are compressed
 * to JPEG so they stay under the server's 25 MB blob limit and the serverless
 * request body limit for OCR. PDFs and already-small images pass through as-is.
 */
export async function prepareFileForUpload(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  if (file.size <= UPLOAD_IMAGE_COMPRESS_THRESHOLD_MB * 1024 * 1024) return file;
  return compressImage(file, {
    maxSizeMB: UPLOAD_IMAGE_COMPRESS_THRESHOLD_MB,
    maxWidthOrHeight: 4000,
    quality: 0.85,
  });
}

async function uploadFileDirect(
  pathname: string,
  file: File,
  handleUploadUrl: string
) {
  try {
    const toUpload = await prepareFileForUpload(file);
    // Compression renames the file (.jpg); keep the stored pathname in sync.
    const finalPathname =
      toUpload.name !== file.name && pathname.endsWith(file.name)
        ? pathname.slice(0, -file.name.length) + toUpload.name
        : pathname;

    const uploaded = await upload(finalPathname, toUpload, {
      access: 'public',
      handleUploadUrl: `${API_BASE_URL}${handleUploadUrl}`,
    });

    return {
      url: uploaded.url,
      pathname: uploaded.pathname,
      contentType: uploaded.contentType,
      sizeBytes: toUpload.size,
    };
  } catch (error) {
    reportApiError(error instanceof Error ? error.message : 'Upload failed');
    throw error;
  }
}

/**
 * Compress an image file using Canvas API
 * Resizes large images and compresses to JPEG for smaller file size
 */
interface CompressOptions {
  maxSizeMB: number;
  maxWidthOrHeight: number;
  quality: number;
}

async function compressImage(file: File, options: CompressOptions): Promise<File> {
  const { maxSizeMB, maxWidthOrHeight, quality } = options;
  
  // If file is already small enough and is JPEG, return as-is
  if (file.size <= maxSizeMB * 1024 * 1024 && file.type === 'image/jpeg') {
    return file;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      try {
        let { width, height } = img;

        // Scale down if dimensions exceed max
        if (width > maxWidthOrHeight || height > maxWidthOrHeight) {
          if (width > height) {
            height = Math.round((height * maxWidthOrHeight) / width);
            width = maxWidthOrHeight;
          } else {
            width = Math.round((width * maxWidthOrHeight) / height);
            height = maxWidthOrHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Draw image to canvas
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob with compression
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }

            // Create new file from blob
            const compressedFile = new File(
              [blob],
              file.name.replace(/\.[^.]+$/, '.jpg'), // Change extension to .jpg
              { type: 'image/jpeg' }
            );

            // If still too large, try again with lower quality
            if (compressedFile.size > maxSizeMB * 1024 * 1024 && quality > 0.3) {
              compressImage(file, { ...options, quality: quality - 0.15 })
                .then(resolve)
                .catch(reject);
            } else {
              resolve(compressedFile);
            }
          },
          'image/jpeg',
          quality
        );
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => reject(new Error('Failed to load image for compression'));

    // Load image from file
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export const api = {
  estimatorHandoffs: (id: string) => request<{ handoffs: { handoffId: string; estimateId: string | null; payload: EstimatorHandoff }[] }>(`/projects/${id}/estimator-handoffs`),
  previewEstimatorHandoff: (id: string, handoff: EstimatorHandoff) => request(`/projects/${id}/estimator-handoffs`, { method: 'POST', body: JSON.stringify({ action: 'preview', handoff }) }),
  importEstimatorHandoff: (id: string, handoff: EstimatorHandoff) => request<{ estimateId: string | null; duplicate: boolean }>(`/projects/${id}/estimator-handoffs`, { method: 'POST', body: JSON.stringify({ action: 'create_draft', handoff }) }),
  estimatorOutcomes: (id: string) => request<{ basis: CostBasis; outcomes: OutcomeEnvelope[] }>(`/projects/${id}/estimator-outcomes`),
  reviewEstimatorOutcome: (id: string, body: unknown) => request<OutcomeEnvelope>(`/projects/${id}/estimator-outcomes`, { method: 'POST', body: JSON.stringify(body) }),

  // silent: Login page renders its own inline form error
  login: (email: string, password: string) => request<{ user: AuthUser }>(`/auth/login`, {
    method: 'POST',
    body: JSON.stringify({ email, password }),
    silent: true,
  }),
  // silent: 401s for every logged-out visitor (AuthContext swallows it)
  me: () => request<{ user: AuthUser }>(`/auth/me`, { silent: true }),
  logout: () => request<{ success: boolean }>(`/auth/logout`, { method: 'POST' }),

  // Project endpoints
  createProject: (projectData: unknown) => request<{ projectId: string; projectNumber: string }>(`/projects`, {
    method: 'POST',
    body: JSON.stringify(projectData),
  }),
  getProject: (id: string) => request<any>(`/projects/${id}`),
  updateProject: (id: string, projectData: unknown) => request<any>(`/projects/${id}`, {
    method: 'PUT',
    body: JSON.stringify(projectData),
  }),
  deleteProject: (id: string) => request<{ success: boolean; message: string }>(`/projects/${id}`, {
    method: 'DELETE',
  }),
  listProjects: (params?: {
    status?: string;
    page?: number;
    limit?: number;
    search?: string;
    excludeIds?: string[];
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.excludeIds?.length) searchParams.append('excludeIds', params.excludeIds.join(','));

    const queryString = searchParams.toString();
    return request<{ projects: any[]; pagination: any }>(`/projects${queryString ? `?${queryString}` : ''}`);
  },

  /** Starred project IDs for the current user, ordered oldest→newest star. */
  getFavoriteProjectIds: () => request<{ projectIds: string[] }>(`/projects/favorites`),
  setProjectFavorite: (projectId: string, isFavorite: boolean) =>
    request<{ projectId: string; isFavorite: boolean }>(`/projects/${projectId}/favorite`, {
      method: 'PUT',
      body: JSON.stringify({ isFavorite }),
    }),

  getProjectPnl: (projectId: string) => request<any>(`/projects/${projectId}/pnl`),
  updateProjectCostForecast: (
    projectId: string,
    body: { remainingWeeks: number; customWeeklyLaborCost?: number | null; notes?: string }
  ) =>
    request<{ forecast: any }>(`/projects/${projectId}/cost-forecast`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  listProjectActuals: (projectId: string) => request<{ actuals: any[] }>(`/projects/${projectId}/actuals`),
  createProjectActual: (projectId: string, body: Record<string, unknown>) =>
    request<any>(`/projects/${projectId}/actuals`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  createLaborTimesheet: (projectId: string, body: Record<string, unknown>) =>
    request<any>(`/projects/${projectId}/actuals/labor/timesheet`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Prior temp labor names for Actuals datalist (authenticated). Use query to avoid /workers/[id] route clash. */
  listTempWorkerLaborNames: () =>
    request<{ names: string[] }>(`/workers?savedTempLaborNames=true`),
  setPrimaryEstimate: (projectId: string, estimateId: string) =>
    request<{ success: boolean }>(`/projects/${projectId}/primary-estimate`, {
      method: 'POST',
      body: JSON.stringify({ estimateId }),
    }),
  uploadProjectActualFile: (projectId: string, file: File) =>
    uploadFileDirect(
      `project-actuals/${projectId}/${Date.now()}-${file.name}`,
      file,
      `/projects/${projectId}/actuals/upload`
    ),
  listReceipts: (params?: { projectId?: string; kind?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.projectId) searchParams.append('projectId', params.projectId);
    if (params?.kind) searchParams.append('kind', params.kind);
    const query = searchParams.toString();
    return request<{ receipts: any[] }>(`/receipts${query ? `?${query}` : ''}`);
  },
  createReceipt: (body: Record<string, unknown>) =>
    request<{ receipt: any }>(`/receipts`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getReceipt: (id: string) => request<{ receipt: any }>(`/receipts/${id}`),
  updateReceipt: (id: string, body: Record<string, unknown>) =>
    request<{ receipt: any }>(`/receipts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deleteReceipt: (id: string) =>
    request<{ success: boolean; deletedId: string }>(`/receipts/${id}`, {
      method: 'DELETE',
    }),
  uploadReceiptFile: (projectId: string, file: File) =>
    uploadFileDirect(
      `project-actuals/${projectId}/receipts/${Date.now()}-${file.name}`,
      file,
      `/receipt-uploads/${projectId}`
    ),
  uploadReceiptBatchFile: (projectId: string, file: File) =>
    uploadFileDirect(
      `project-actuals/${projectId}/receipt-batches/${Date.now()}-${file.name}`,
      file,
      `/receipt-batch-uploads/${projectId}`
    ),
  createReceiptBatch: (body: { projectId: string; uploads: Array<{ url: string; filename: string; contentType: string; sizeBytes: number }> }) =>
    request<{ batch: any; workerTrigger?: any }>(`/receipt-batches`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listReceiptBatches: (params?: { projectId?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.projectId) searchParams.append('projectId', params.projectId);
    const query = searchParams.toString();
    return request<{ batches: any[] }>(`/receipt-batches${query ? `?${query}` : ''}`);
  },
  getReceiptBatch: (batchId: string) => request<{ batch: any }>(`/receipt-batches/${batchId}`),
  processReceiptBatch: (batchId: string, options?: { resetFailed?: boolean }) =>
    request<{ batch: any; workerTrigger: any; resetFailedItems: number }>(`/receipt-batches/${batchId}/process`, {
      method: 'POST',
      body: JSON.stringify(options || {}),
    }),
  listReceiptBatchItems: (batchId: string) => request<{ items: any[] }>(`/receipt-batches/${batchId}/items`),
  approveReceiptBatchItem: (batchId: string, itemId: string, body: Record<string, unknown>) =>
    request<{ item: any; receipt: any }>(`/receipt-batches/${batchId}/items/${itemId}/approve`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  excludeReceiptBatchItem: (batchId: string, itemId: string, reason: string) =>
    request<{ item: any }>(`/receipt-batches/${batchId}/items/${itemId}/exclude`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  getCalendarActuals: (params: { startDate: string; endDate: string; projectId?: string }) => {
    const searchParams = new URLSearchParams();
    searchParams.append('startDate', params.startDate);
    searchParams.append('endDate', params.endDate);
    if (params.projectId) searchParams.append('projectId', params.projectId);
    return request<{ days: Array<{ date: string; totalHours: number; totalCost: number }> }>(
      `/calendar/actuals?${searchParams.toString()}`
    );
  },
  getCalendarOperations: (params: {
    startDate: string;
    endDate: string;
    projectId?: string;
    crewId?: string;
    workerId?: string;
  }) => {
    const searchParams = new URLSearchParams();
    searchParams.append('startDate', params.startDate);
    searchParams.append('endDate', params.endDate);
    if (params.projectId) searchParams.append('projectId', params.projectId);
    if (params.crewId) searchParams.append('crewId', params.crewId);
    if (params.workerId) searchParams.append('workerId', params.workerId);
    return request<any>(`/calendar/operations?${searchParams.toString()}`);
  },

  // Estimate endpoints
  createEstimate: (estimateData: unknown) => request<any>(`/estimates/create`, {
    method: 'POST',
    body: JSON.stringify(estimateData),
  }),
  getEstimate: (id: string) => request<any>(`/estimates/${id}`),
  updateEstimate: (id: string, estimateData: unknown) => request<any>(`/estimates/${id}/update`, {
    method: 'PUT',
    body: JSON.stringify(estimateData),
  }),
  deleteEstimate: (id: string) => request<{ success: boolean; deletedId: string }>(`/estimates/${id}/delete`, {
    method: 'DELETE',
  }),
  duplicateEstimate: (id: string) => request<{ success: boolean; id: string; estimateNumber: string }>(`/estimates/${id}/duplicate`, {
    method: 'POST',
  }),
  uploadEstimatePdf: async (
    estimateId: string,
    file: File
  ): Promise<{ url: string; pathname: string; contentType: string; sizeBytes: number }> => {
    const uploaded = await uploadFileDirect(
      `estimate-pdfs/${estimateId}/${Date.now()}-${file.name}`,
      file,
      `/estimates/upload`
    );

    return {
      url: uploaded.url,
      pathname: uploaded.pathname,
      contentType: uploaded.contentType,
      sizeBytes: uploaded.sizeBytes,
    };
  },
  saveEstimatePdf: (
    estimateId: string,
    data: import('../types/estimate-pdf').UpsertEstimatePdfInput
  ) =>
    request<import('../types/estimate-pdf').EstimatePdfArtifact>(`/estimates/${estimateId}/pdfs`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  listEstimates: (params?: {
    projectId?: string;
    status?: string;
    kind?: string;
    kinds?: string;
    awaitingClient?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.projectId) searchParams.append('projectId', params.projectId);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.kind) searchParams.append('kind', params.kind);
    if (params?.kinds) searchParams.append('kinds', params.kinds);
    if (params?.awaitingClient) searchParams.append('awaitingClient', 'true');
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());

    const queryString = searchParams.toString();
    return request<{ estimates: any[]; pagination: any }>(`/estimates${queryString ? `?${queryString}` : ''}`);
  },

  // Inspection endpoints
  listInspections: (params?: { projectId?: string; status?: string; limit?: number; offset?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.projectId) searchParams.append('projectId', params.projectId);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());

    const queryString = searchParams.toString();
    return request<{ inspections: any[]; pagination: any }>(`/inspections${queryString ? `?${queryString}` : ''}`);
  },
  getInspection: (id: string) => request<any>(`/inspections/${id}`),
  createInspection: (data: {
    projectId: string;
    jobName?: string;
    locationAddress?: string | null;
    areaDescription?: string;
    inspectorName: string;
    inspectorLicense?: string;
    inspectionDate: string;
    certificationStatement: string;
  }) => request<any>(`/inspections/create`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  uploadInspectionPdf: async (
    projectId: string,
    file: File
  ): Promise<{ url: string; pathname: string; contentType: string; sizeBytes: number }> => {
    const uploaded = await uploadFileDirect(
      `project-inspections/${projectId}/${Date.now()}-${file.name}`,
      file,
      `/inspections/upload`
    );

    return {
      url: uploaded.url,
      pathname: uploaded.pathname,
      contentType: uploaded.contentType,
      sizeBytes: uploaded.sizeBytes,
    };
  },
  createUploadedInspection: (data: {
    projectId: string;
    areaDescription?: string | null;
    inspectionDate?: string | null;
    sourcePdfUrl: string;
    sourcePdfFilename?: string | null;
    sourcePdfUploadedAt?: string | null;
    sourcePdfContentType?: string | null;
    sourcePdfSizeBytes?: number | null;
  }) => request<any>(`/inspections/create`, {
    method: 'POST',
    body: JSON.stringify({ sourceType: 'uploaded', ...data }),
  }),
  updateInspection: (id: string, data: {
    jobName?: string;
    locationAddress?: string | null;
    areaDescription?: string | null;
    inspectorName?: string;
    inspectorLicense?: string | null;
    inspectionDate?: string;
    certificationStatement?: string;
    status?: 'draft' | 'issued';
  }) => request<any>(`/inspections/${id}/update`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteInspection: (id: string) => request<{ success: boolean; deletedId: string }>(`/inspections/${id}/delete`, {
    method: 'DELETE',
  }),

  listBillingContracts: (projectId: string) => request<{ contracts: { id: string; name: string }[] }>(`/billing-contracts?projectId=${projectId}`),
  getBillingContract: (id: string) => request<import('../../api/lib/aia/model').BillingContract>(`/billing-contracts/${id}`),
  createBillingContract: (data: { projectIds: string[]; settings: import('../../api/lib/aia/model').ContractSettings; seedShippingport: boolean }) => request<import('../../api/lib/aia/model').BillingContract>('/billing-contracts', { method: 'POST', body: JSON.stringify(data) }),
  billingAction: (id: string, data: Record<string, unknown>) => request<import('../../api/lib/aia/model').BillingContract>(`/billing-contracts/${id}`, { method: 'POST', body: JSON.stringify(data) }),
  billingCosts: (id: string, from: string, to: string, number: number) => request<{ costs: import('../../api/lib/aia/model').AiaCost[]; warnings: string[] }>(`/billing-contracts/${id}?action=costs&from=${from}&to=${to}&number=${number}`),
  billingDocuments: (id: string) => request<{ documents: import('../../api/lib/aia/model').AssignmentPdf[] }>(`/billing-contracts/${id}?action=documents`),

  // Project payment app endpoints
  listProjectPaymentApps: (params?: {
    projectId?: string;
    status?: import('../types/project-payment-app').ProjectPaymentAppStatus;
    limit?: number;
    offset?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.projectId) searchParams.append('projectId', params.projectId);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());

    const queryString = searchParams.toString();
    return request<{
      apps: import('../types/project-payment-app').ProjectPaymentApp[];
      summary: {
        appCount: number;
        totalSubmittedAmount: number;
        totalPaidAmount: number;
      };
      pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
      };
    }>(`/project-payment-apps${queryString ? `?${queryString}` : ''}`);
  },
  getProjectPaymentApp: (id: string) =>
    request<import('../types/project-payment-app').ProjectPaymentApp & {
      project?: { id: string; name: string; internalProjectNumber: string | null };
    }>(`/project-payment-apps/${id}`),
  createProjectPaymentApp: (data: import('../types/project-payment-app').CreateProjectPaymentAppInput) =>
    request<import('../types/project-payment-app').ProjectPaymentApp>(`/project-payment-apps`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateProjectPaymentApp: (id: string, data: import('../types/project-payment-app').UpdateProjectPaymentAppInput) =>
    request<import('../types/project-payment-app').ProjectPaymentApp>(`/project-payment-apps/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteProjectPaymentApp: (id: string) =>
    request<{ success: boolean; deletedId: string }>(`/project-payment-apps/${id}`, {
      method: 'DELETE',
    }),
  uploadProjectPaymentAppPdf: async (
    file: File
  ): Promise<{ url: string; pathname: string; contentType: string; sizeBytes: number }> => {
    const uploaded = await uploadFileDirect(
      `project-payment-apps/${Date.now()}-${file.name}`,
      file,
      `/project-payment-apps/upload`
    );

    return {
      url: uploaded.url,
      pathname: uploaded.pathname,
      contentType: uploaded.contentType,
      sizeBytes: uploaded.sizeBytes,
    };
  },

  // Billing request (billing work order) endpoints — admin only
  listBillingRequests: (params?: {
    status?: import('../types/billing-request').BillingRequestStatus;
    assignedTo?: string;
    projectId?: string;
    limit?: number;
    offset?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.assignedTo) searchParams.append('assignedTo', params.assignedTo);
    if (params?.projectId) searchParams.append('projectId', params.projectId);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());

    const queryString = searchParams.toString();
    return request<{
      billingRequests: import('../types/billing-request').BillingRequest[];
      pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
      };
    }>(`/billing-requests${queryString ? `?${queryString}` : ''}`);
  },
  getBillingRequest: (id: string) =>
    request<import('../types/billing-request').BillingRequest>(`/billing-requests/${id}`),
  createBillingRequest: (data: import('../types/billing-request').CreateBillingRequestInput) =>
    request<import('../types/billing-request').BillingRequest>(`/billing-requests`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateBillingRequest: (id: string, data: import('../types/billing-request').UpdateBillingRequestInput) =>
    request<import('../types/billing-request').BillingRequest>(`/billing-requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteBillingRequest: (id: string) =>
    request<{ success: boolean }>(`/billing-requests/${id}`, {
      method: 'DELETE',
    }),

  // Lab sample result endpoints
  listLabSampleResults: (params?: {
    projectId?: string;
    status?: import('../types/lab-sample-result').LabSampleResultStatus;
    limit?: number;
    offset?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.projectId) searchParams.append('projectId', params.projectId);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());

    const queryString = searchParams.toString();
    return request<{
      results: import('../types/lab-sample-result').LabSampleResult[];
      summary: {
        resultCount: number;
        uploadedCount: number;
        draftCount: number;
        exportedCount: number;
      };
      pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
      };
    }>(`/lab-sample-results${queryString ? `?${queryString}` : ''}`);
  },
  getLabSampleResult: (id: string) =>
    request<import('../types/lab-sample-result').LabSampleResult & {
      project?: { id: string; name: string; internalProjectNumber: string | null };
    }>(`/lab-sample-results/${id}`),
  createLabSampleResult: (data: import('../types/lab-sample-result').CreateLabSampleResultInput) =>
    request<import('../types/lab-sample-result').LabSampleResult>(`/lab-sample-results`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateLabSampleResult: (id: string, data: import('../types/lab-sample-result').UpdateLabSampleResultInput) =>
    request<import('../types/lab-sample-result').LabSampleResult>(`/lab-sample-results/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteLabSampleResult: (id: string) =>
    request<{ success: boolean; deletedId: string }>(`/lab-sample-results/${id}`, {
      method: 'DELETE',
    }),
  generateLabSampleSurveyDraft: (id: string) =>
    request<import('../types/lab-sample-result').LabSampleResult>(`/lab-sample-results/${id}/generate`, {
      method: 'POST',
    }),
  uploadLabSampleResultPdf: async (
    projectId: string,
    file: File
  ): Promise<{ url: string; pathname: string; contentType: string; sizeBytes: number }> => {
    const uploaded = await uploadFileDirect(
      `lab-sample-results/${projectId}/${Date.now()}-${file.name}`,
      file,
      `/lab-sample-results/upload`
    );

    return {
      url: uploaded.url,
      pathname: uploaded.pathname,
      contentType: uploaded.contentType,
      sizeBytes: uploaded.sizeBytes,
    };
  },
  uploadSurveyReportPdf: async (
    projectId: string,
    labSampleResultId: string,
    file: File
  ): Promise<{ url: string; pathname: string; contentType: string; sizeBytes: number }> => {
    const uploaded = await uploadFileDirect(
      `lab-sample-results/${projectId}/survey-reports/${labSampleResultId}/${Date.now()}-${file.name}`,
      file,
      `/lab-sample-results/upload`
    );

    return {
      url: uploaded.url,
      pathname: uploaded.pathname,
      contentType: uploaded.contentType,
      sizeBytes: uploaded.sizeBytes,
    };
  },

  // Export/Import endpoints
  exportProjects: async () => {
    const response = await fetch(`${API_BASE_URL}/projects/export`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      const error = new Error(err.error || `Export failed: ${response.status}`);
      reportApiError(error.message);
      throw error;
    }
    return response; // Return the raw response so we can access the blob
  },

  importProjects: (jsonData: unknown) => request<{ message: string; stats: any }>(`/projects/import`, {
    method: 'POST',
    body: JSON.stringify(jsonData),
  }),

  // Orders endpoints
  listOrders: (params?: { status?: 'draft' | 'submitted' | 'confirmed' | 'billed' | 'completed' | 'cancelled'; jobNumber?: string; projectId?: string; limit?: number; offset?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.jobNumber) searchParams.append('jobNumber', params.jobNumber);
    if (params?.projectId) searchParams.append('projectId', params.projectId);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    const qs = searchParams.toString();
    return request<any[]>(`/orders${qs ? `?${qs}` : ''}`);
  },
  createOrder: (data: import('../types/orders').CreateOrderRequest) => request<import('../types/orders').OrderResponse>(`/orders`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getOrder: (id: string) => request<import('../types/orders').OrderResponse>(`/orders/${id}`),
  updateOrder: (id: string, updates: import('../types/orders').UpdateOrderRequest) => request<import('../types/orders').OrderResponse>(`/orders/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  deleteOrder: (id: string) => request<{ message: string; id: string }>(`/orders/${id}`, { method: 'DELETE' }),
  exportOrderPdf: (id: string) => request<{ success: boolean; order: import('../types/orders').OrderResponse; format: 'pdf' }>(`/orders/${id}/export/pdf`),
  uploadOrderInvoice: (orderId: string, file: File) =>
    uploadFileDirect(
      `order-invoices/${orderId}/${Date.now()}-${file.name}`,
      file,
      `/orders/${orderId}/invoice/upload`
    ),
  recordOrderInvoice: (orderId: string, data: import('../types/orders').RecordOrderInvoiceRequest) =>
    request<{ success: boolean; orderId: string; totalAmount: number; inventoryUpdatesApplied: number }>(`/orders/${orderId}/invoice`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // ESSCO Catalog endpoints
  getEsscoCatalog: () => request<{ products: any[] }>(`/essco`),
  getEsscoProduct: (id: string) => request<any>(`/essco/${id}`),
  updateEsscoProduct: (id: string, data: { 
    esscoId?: string;
    name?: string;
    description?: string | null;
    price?: number | null; 
    unit?: string; 
    isInStock?: boolean; 
    isFavorite?: boolean;
  }) => 
    request<any>(`/essco/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  duplicateEsscoProduct: (id: string) => request<any>(`/essco/${id}`, { method: 'POST' }),
  deleteEsscoProduct: (id: string) => request<{ success: boolean; id: string }>(`/essco/${id}`, { method: 'DELETE' }),
  
  exportEsscoCatalog: async () => {
    const response = await fetch(`${API_BASE_URL}/essco/export`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      const error = new Error(err.error || `Export failed: ${response.status}`);
      reportApiError(error.message);
      throw error;
    }
    return response;
  },
  
  importEsscoCatalog: (products: any[], mode: 'merge' | 'replace' = 'merge') => 
    request<{ success: boolean; stats: { inserted: number; updated: number; skipped: number; total: number } }>(`/essco`, {
      method: 'POST',
      body: JSON.stringify({ products, mode }),
    }),

  // Crews endpoints
  listCrews: (includeInactive?: boolean) => {
    const params = includeInactive ? '?includeInactive=true' : '';
    return request<{ crews: any[] }>(`/crews${params}`);
  },
  getCrew: (id: string) => request<{ crew: any }>(`/crews/${id}`),
  createCrew: (data: unknown) => request<{ crew: any }>(`/crews`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateCrew: (id: string, data: unknown) => request<{ crew: any }>(`/crews/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteCrew: (id: string) => request<{ success: boolean; deletedId: string }>(`/crews/${id}`, {
    method: 'DELETE',
  }),

  // Workers endpoints
  listWorkers: (activeOnly?: boolean) => {
    const params = activeOnly ? '?activeOnly=true' : '';
    return request<{ workers: any[] }>(`/workers${params}`);
  },
  getWorker: (id: string) => request<{ worker: any }>(`/workers/${id}`),
  getWorkerProfile: (id: string) => request<any>(`/workers/${id}/profile`),
  createWorker: (data: unknown) => request<{ worker: any }>(`/workers`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateWorker: (id: string, data: unknown) => request<{ worker: any }>(`/workers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteWorker: (id: string) => request<{ success: boolean; deletedId: string }>(`/workers/${id}`, {
    method: 'DELETE',
  }),
  uploadWorkerDocument: (workerId: string, file: File) =>
    uploadFileDirect(
      `worker-documents/${workerId}/${Date.now()}-${file.name}`,
      file,
      `/workers/${workerId}/documents/upload`
    ),
  createWorkerDocument: (workerId: string, data: unknown) =>
    request<{ document: any }>(`/workers/${workerId}/documents`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateWorkerDocument: (workerId: string, documentId: string, data: unknown) =>
    request<{ document: any }>(`/workers/${workerId}/documents/${documentId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteWorkerDocument: (workerId: string, documentId: string) =>
    request<{ success: boolean; deletedId: string }>(`/workers/${workerId}/documents/${documentId}`, {
      method: 'DELETE',
    }),
  createWorkerCredential: (workerId: string, data: unknown) =>
    request<{ credential: any }>(`/workers/${workerId}/credentials`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateWorkerCredential: (workerId: string, credentialId: string, data: unknown) =>
    request<{ credential: any }>(`/workers/${workerId}/credentials/${credentialId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteWorkerCredential: (workerId: string, credentialId: string) =>
    request<{ success: boolean; deletedId: string }>(`/workers/${workerId}/credentials/${credentialId}`, {
      method: 'DELETE',
    }),
  getProjectActual: (projectId: string, actualId: string) =>
    request<{ actual: any }>(`/projects/${projectId}/actuals/${actualId}`),

  // Assignments endpoints
  listAssignments: (params?: { startDate?: string; endDate?: string; projectId?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    if (params?.projectId) searchParams.append('projectId', params.projectId);
    const qs = searchParams.toString();
    return request<{ assignments: any[] }>(`/assignments${qs ? `?${qs}` : ''}`);
  },
  getAssignment: (id: string) => request<{ assignment: any }>(`/assignments/${id}`),
  createAssignment: (data: unknown) => request<{ assignment: any }>(`/assignments`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateAssignment: (id: string, data: unknown) => request<{ assignment: any }>(`/assignments/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteAssignment: (id: string) => request<{ success: boolean; deletedId: string }>(`/assignments/${id}`, {
    method: 'DELETE',
  }),
  listAssignmentActualHours: (id: string) =>
    request<{ lines: any[] }>(`/assignments/${id}/actual-hours`),
  updateAssignmentActualHours: (id: string, data: unknown) =>
    request<{ lines: any[] }>(`/assignments/${id}/actual-hours`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  listAssignmentTimesheets: (id: string) =>
    request<{ timesheets: any[] }>(`/assignments/${id}/timesheets`),
  createAssignmentTimesheet: (id: string, data: unknown) =>
    request<{ timesheet: any }>(`/assignments/${id}/timesheets`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  deleteAssignmentTimesheet: (assignmentId: string, timesheetId: string) =>
    request<{ success: boolean; deletedId: string }>(`/assignments/${assignmentId}/timesheets/${timesheetId}`, {
      method: 'DELETE',
    }),
  uploadAssignmentTimesheet: (assignmentId: string, file: File) =>
    uploadFileDirect(
      `assignment-timesheets/${assignmentId}/${Date.now()}-${file.name}`,
      file,
      `/assignments/${assignmentId}/timesheets/upload`
    ),
  listAssignmentInvoices: (id: string) =>
    request<{ invoices: any[] }>(`/assignments/${id}/invoices`),
  createAssignmentInvoice: (id: string, data: unknown) =>
    request<{ invoice: any }>(`/assignments/${id}/invoices`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  deleteAssignmentInvoice: (assignmentId: string, invoiceId: string) =>
    request<{ success: boolean; deletedId: string }>(`/assignments/${assignmentId}/invoices/${invoiceId}`, {
      method: 'DELETE',
    }),
  uploadAssignmentInvoice: (assignmentId: string, file: File) =>
    uploadFileDirect(
      `assignment-invoices/${assignmentId}/${Date.now()}-${file.name}`,
      file,
      `/assignments/${assignmentId}/invoices/upload`
    ),
  // silent (no reportApiError): callers deliberately fall back to manual entry on OCR failure
  ocrInvoice: async (file: File) => {
    const toSend = await prepareFileForUpload(file);
    const form = new FormData();
    form.append('file', toSend);
    const res = await fetch(`${API_BASE_URL}/ocr/invoice`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { error?: string }).error || `Invoice OCR failed: ${res.status}`);
    }
    return res.json() as Promise<{
      invoiceDate: string | null;
      total: number | null;
      ocrData: Record<string, unknown>;
    }>;
  },

  // Calendar Events endpoints
  listCalendarEvents: (params?: { startDate?: string; endDate?: string; assignmentId?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    if (params?.assignmentId) searchParams.append('assignmentId', params.assignmentId);
    const qs = searchParams.toString();
    return request<{ events: any[] }>(`/calendar-events${qs ? `?${qs}` : ''}`);
  },
  createCalendarEvent: (data: unknown) => request<{ event: any }>(`/calendar-events`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateCalendarEvent: (id: string, data: unknown) => request<{ event: any }>(`/calendar-events/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteCalendarEvent: (id: string) => request<{ success: boolean; deletedId: string }>(`/calendar-events/${id}`, {
    method: 'DELETE',
  }),

  // Companies endpoints
  listCompanies: (params?: { search?: string; isActive?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append('search', params.search);
    if (params?.isActive !== undefined) searchParams.append('isActive', params.isActive.toString());
    const qs = searchParams.toString();
    return request<any[]>(`/companies${qs ? `?${qs}` : ''}`);
  },
  getCompany: (id: string) => request<any>(`/companies/${id}`),
  createCompany: (data: unknown) => request<any>(`/companies`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateCompany: (id: string, data: unknown) => request<any>(`/companies/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteCompany: (id: string) => request<{ success: boolean; message: string }>(`/companies/${id}`, {
    method: 'DELETE',
  }),

  // Contacts endpoints
  listAllContacts: (params?: { search?: string; companyId?: string; standalone?: boolean; isActive?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append('search', params.search);
    if (params?.companyId) searchParams.append('companyId', params.companyId);
    if (params?.standalone !== undefined) searchParams.append('standalone', params.standalone.toString());
    if (params?.isActive !== undefined) searchParams.append('isActive', params.isActive.toString());
    const qs = searchParams.toString();
    return request<any[]>(`/contacts${qs ? `?${qs}` : ''}`);
  },
  listContacts: (companyId: string) => request<any[]>(`/companies/${companyId}/contacts`),
  createContact: (data: { companyId?: string | null; firstName: string; lastName: string; title?: string; email?: string; phone?: string; isPrimary?: boolean }) => 
    request<any>(`/contacts`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getContact: (id: string) => request<any>(`/contacts/${id}`),
  updateContact: (id: string, data: unknown) => request<any>(`/contacts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteContact: (id: string) => request<{ success: boolean; message: string }>(`/contacts/${id}`, {
    method: 'DELETE',
  }),

  // Floor Plans endpoints
  listFloorPlans: (projectId: string) => 
    request<{ floorPlans: import('../types/floor-plan').FloorPlan[] }>(`/floor-plans?projectId=${projectId}`),
  
  getFloorPlan: (id: string) => 
    request<import('../types/floor-plan').FloorPlan>(`/floor-plans/${id}`),
  
  createFloorPlan: (projectId: string, data: import('../types/floor-plan').CreateFloorPlanInput) => 
    request<import('../types/floor-plan').FloorPlan>(`/floor-plans`, {
      method: 'POST',
      body: JSON.stringify({ ...data, projectId }),
    }),
  
  updateFloorPlan: (id: string, data: import('../types/floor-plan').UpdateFloorPlanInput) => 
    request<import('../types/floor-plan').FloorPlan>(`/floor-plans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  deleteFloorPlan: (id: string) => 
    request<{ success: boolean; message: string }>(`/floor-plans/${id}`, {
      method: 'DELETE',
    }),

// Deals / CRM endpoints
  listDeals: (params?: {
    stage?: string;
    status?: string;
    ownerId?: string;
    companyId?: string;
    projectType?: string;
    buildingType?: string;
    bidSource?: string;
    search?: string;
    includeArchived?: boolean;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.stage) searchParams.append('stage', params.stage);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.ownerId) searchParams.append('ownerId', params.ownerId);
    if (params?.companyId) searchParams.append('companyId', params.companyId);
    if (params?.projectType) searchParams.append('projectType', params.projectType);
    if (params?.buildingType) searchParams.append('buildingType', params.buildingType);
    if (params?.bidSource) searchParams.append('bidSource', params.bidSource);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.includeArchived) searchParams.append('includeArchived', 'true');
    const qs = searchParams.toString();
    return request<DealSummary[]>(`/deals${qs ? `?${qs}` : ''}`);
  },
  getDeal: (id: string) => request<DealDetailView>(`/deals/${id}`),
  createDeal: (data: unknown) => request<DealDetailView>(`/deals`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateDeal: (id: string, data: unknown) => request<DealDetailView>(`/deals/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  updateDealStage: (id: string, stage: string) => request<{ success: boolean }>(`/deals/${id}/stage`, {
    method: 'PATCH',
    body: JSON.stringify({ stage }),
  }),
  archiveDeal: (id: string, archived: boolean) => request<{ success: boolean; id: string; isArchived: boolean; message: string }>(`/deals/${id}/archive`, {
    method: 'PUT',
    body: JSON.stringify({ archived }),
  }),
  deleteDeal: (id: string) => request<{ success: boolean; message: string }>(`/deals/${id}`, {
    method: 'DELETE',
  }),
  createProjectFromDeal: (dealId: string) => 
    request<{ success: boolean; projectId: string; projectNumber: string }>(`/deals/${dealId}/create-project`, {
      method: 'POST',
    }),
  linkDealProject: (dealId: string, projectId: string) =>
    request<{ success: boolean; projectId: string; projectNumber: string | null }>(`/deals/${dealId}/link-project`, {
      method: 'POST',
      body: JSON.stringify({ projectId }),
    }),
  unlinkDealProject: (dealId: string) =>
    request<{ success: boolean; projectId: null }>(`/deals/${dealId}/link-project`, {
      method: 'DELETE',
    }),
  createDealFromProject: (projectId: string, data?: { name?: string; stage?: string }) =>
    request<{ success: boolean; dealId: string; projectId: string }>(`/projects/${projectId}/create-deal`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    }),
  listComposedInvoices: (projectId: string) =>
    request<{ invoices: import('../../api/lib/schemas').ComposedInvoice[] }>(
      `/projects/${projectId}/composed-invoices`
    ),
  createComposedInvoice: (projectId: string, data: { estimateIds: string[]; title?: string }) =>
    request<import('../../api/lib/schemas').ComposedInvoice>(`/projects/${projectId}/composed-invoices`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getComposedInvoice: (id: string) =>
    request<import('../../api/lib/schemas').ComposedInvoice>(`/composed-invoices/${id}`),
  saveComposedInvoicePdf: (
    id: string,
    data: { pdfUrl: string; pdfFilename: string; pdfContentType?: string | null; pdfSizeBytes?: number | null }
  ) =>
    request<import('../../api/lib/schemas').ComposedInvoice>(`/composed-invoices/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  uploadComposedInvoicePdf: async (invoiceId: string, file: File) => {
    const uploaded = await uploadFileDirect(
      `composed-invoices/${invoiceId}/${Date.now()}-${file.name}`,
      file,
      `/composed-invoices/upload`
    );
    return {
      url: uploaded.url,
      pathname: uploaded.pathname,
      contentType: uploaded.contentType,
      sizeBytes: uploaded.sizeBytes,
    };
  },

  // Deal Activities endpoints
  listDealActivities: (dealId: string) => request<any[]>(`/deals/${dealId}/activities`),
  createDealActivity: (dealId: string, data: unknown) => request<any>(`/deals/${dealId}/activities`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateDealActivity: (dealId: string, activityId: string, data: unknown) => 
    request<any>(`/deals/${dealId}/activities?activityId=${activityId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteDealActivity: (dealId: string, activityId: string) => 
    request<{ success: boolean }>(`/deals/${dealId}/activities?activityId=${activityId}`, {
      method: 'DELETE',
    }),

  // Tasks endpoints (unified: deal, company, contact, todo tasks)
  listTasks: (params?: { 
    assignedTo?: string; 
    showAll?: boolean; 
    status?: 'open' | 'completed' | 'all';
    priority?: 'low' | 'medium' | 'high';
    sourceType?: 'deal' | 'company' | 'contact' | 'todo' | 'relationships' | 'all';
    overdue?: boolean;
    dueBefore?: string;
    dueAfter?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.assignedTo) searchParams.append('assignedTo', params.assignedTo);
    if (params?.showAll) searchParams.append('showAll', 'true');
    if (params?.status) searchParams.append('status', params.status);
    if (params?.priority) searchParams.append('priority', params.priority);
    if (params?.sourceType) searchParams.append('sourceType', params.sourceType);
    if (params?.overdue) searchParams.append('overdue', 'true');
    if (params?.dueBefore) searchParams.append('dueBefore', params.dueBefore);
    if (params?.dueAfter) searchParams.append('dueAfter', params.dueAfter);
    const qs = searchParams.toString();
    return request<any[]>(`/tasks${qs ? `?${qs}` : ''}`);
  },
  listDealTasks: (dealId: string) => request<any[]>(`/deals/${dealId}/tasks`),
  createDealTask: (dealId: string, data: unknown) => request<any>(`/deals/${dealId}/tasks`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getTask: (id: string) => request<any>(`/tasks/${id}`),
  updateTask: (id: string, data: unknown) => request<any>(`/tasks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteTask: (id: string) => request<{ success: boolean; message: string }>(`/tasks/${id}`, {
    method: 'DELETE',
  }),

  // User Tasks (standalone todos) endpoints
  listUserTasks: (params?: {
    showAll?: boolean;
    status?: 'open' | 'completed' | 'all';
    priority?: 'low' | 'medium' | 'high';
    overdue?: boolean;
    dueBefore?: string;
    dueAfter?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.showAll) searchParams.append('showAll', 'true');
    if (params?.status) searchParams.append('status', params.status);
    if (params?.priority) searchParams.append('priority', params.priority);
    if (params?.overdue) searchParams.append('overdue', 'true');
    if (params?.dueBefore) searchParams.append('dueBefore', params.dueBefore);
    if (params?.dueAfter) searchParams.append('dueAfter', params.dueAfter);
    const qs = searchParams.toString();
    return request<any[]>(`/user-tasks${qs ? `?${qs}` : ''}`);
  },
  createUserTask: (data: {
    title: string;
    description?: string;
    dueDate?: string;
    priority?: 'low' | 'medium' | 'high';
    taskType?: string;
    assignedTo?: string;
  }) => request<any>(`/user-tasks`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getUserTask: (id: string) => request<any>(`/user-tasks/${id}`),
  updateUserTask: (id: string, data: unknown) => request<any>(`/user-tasks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteUserTask: (id: string) => request<{ success: boolean; message: string }>(`/user-tasks/${id}`, {
    method: 'DELETE',
  }),

  // Users endpoint (for assignment dropdowns)
  listUsers: () => request<{ id: string; name: string; email: string }[]>(`/users`),

  // Relationships endpoints
  listRelationships: (params?: {
    stage?: string;
    ownerId?: string;
    type?: 'company' | 'individual' | 'all';
    search?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.stage) searchParams.append('stage', params.stage);
    if (params?.ownerId) searchParams.append('ownerId', params.ownerId);
    if (params?.type) searchParams.append('type', params.type);
    if (params?.search) searchParams.append('search', params.search);
    const qs = searchParams.toString();
    return request<{ items: any[]; total: number }>(`/relationships${qs ? `?${qs}` : ''}`);
  },

  // Company Activities endpoints
  listCompanyActivities: (companyId: string) => request<any[]>(`/companies/${companyId}/activities`),
  createCompanyActivity: (companyId: string, data: unknown) => request<any>(`/companies/${companyId}/activities`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  deleteCompanyActivity: (companyId: string, activityId: string) => 
    request<{ success: boolean }>(`/companies/${companyId}/activities?activityId=${activityId}`, {
      method: 'DELETE',
    }),

  // Company Tasks endpoints
  listCompanyTasks: (companyId: string) => request<any[]>(`/companies/${companyId}/tasks`),
  createCompanyTask: (companyId: string, data: unknown) => request<any>(`/companies/${companyId}/tasks`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateCompanyTask: (companyId: string, taskId: string, data: unknown) => 
    request<any>(`/companies/${companyId}/tasks?taskId=${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteCompanyTask: (companyId: string, taskId: string) => 
    request<{ success: boolean }>(`/companies/${companyId}/tasks?taskId=${taskId}`, {
      method: 'DELETE',
    }),

  // Company Stage endpoint
  updateCompanyStage: (companyId: string, relationshipStage: string) => 
    request<{ success: boolean; relationshipStage: string }>(`/companies/${companyId}/stage`, {
      method: 'PUT',
      body: JSON.stringify({ relationshipStage }),
    }),

  // Contact Activities endpoints
  listContactActivities: (contactId: string) => request<any[]>(`/contacts/${contactId}/activities`),
  createContactActivity: (contactId: string, data: unknown) => request<any>(`/contacts/${contactId}/activities`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  deleteContactActivity: (contactId: string, activityId: string) => 
    request<{ success: boolean }>(`/contacts/${contactId}/activities?activityId=${activityId}`, {
      method: 'DELETE',
    }),

  // Contact Tasks endpoints
  listContactTasks: (contactId: string) => request<any[]>(`/contacts/${contactId}/tasks`),
  createContactTask: (contactId: string, data: unknown) => request<any>(`/contacts/${contactId}/tasks`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateContactTask: (contactId: string, taskId: string, data: unknown) => 
    request<any>(`/contacts/${contactId}/tasks?taskId=${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteContactTask: (contactId: string, taskId: string) => 
    request<{ success: boolean }>(`/contacts/${contactId}/tasks?taskId=${taskId}`, {
      method: 'DELETE',
    }),

  // Contact Stage endpoint
  updateContactStage: (contactId: string, relationshipStage: string) => 
    request<{ success: boolean; relationshipStage: string }>(`/contacts/${contactId}/stage`, {
      method: 'PUT',
      body: JSON.stringify({ relationshipStage }),
    }),

  // Site Scout (read-only viewer + share management; structure is written only
  // by the iPad app's sync — the web can additionally set/clear a project link)
  listSiteScoutJobs: (options?: { projectId?: string; linked?: boolean }) => {
    const params = new URLSearchParams();
    if (options?.projectId) params.set('projectId', options.projectId);
    if (options?.linked !== undefined) params.set('linked', String(options.linked));
    const qs = params.toString();
    return request<{ jobs: SiteScoutJobSummary[] }>(`/site-scout${qs ? `?${qs}` : ''}`);
  },
  getSiteScoutJob: (id: string) =>
    request<{ job: JobTree; project: SiteScoutLinkedProject | null }>(`/site-scout/${id}`),
  updateSiteScoutJobProject: (jobId: string, projectId: string | null) =>
    request<{ ok: boolean; projectId: string | null; project: SiteScoutLinkedProject | null }>(
      `/site-scout/${jobId}`,
      { method: 'PATCH', body: JSON.stringify({ projectId }) }
    ),
  listSiteScoutShares: (jobId: string) =>
    request<{ shares: SiteScoutShare[] }>(`/site-scout/${jobId}/shares`),
  createSiteScoutShare: (jobId: string, input: CreateSiteScoutShareInput) =>
    request<{ share: SiteScoutShareCreated }>(`/site-scout/${jobId}/shares`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  revokeSiteScoutShare: (shareId: string) =>
    request<{ ok: boolean }>(`/site-scout/shares/${shareId}`, { method: 'DELETE' }),
  // silent: public page renders its own not-found state
  getSiteScoutSharedView: (token: string) =>
    request<{ label: string; job: JobTree }>(`/share/site-scout/${token}`, { silent: true }),

  uploadFloorPlanImage: async (file: File): Promise<{ url: string; pathname: string }> => {
    // Compress large images before direct upload to keep storage and bandwidth reasonable.
    const compressedFile = await compressImage(file, {
      maxSizeMB: 2.5, // Target size (leaves room for base64 overhead)
      maxWidthOrHeight: 4000, // Max dimension - floor plans need detail
      quality: 0.85,
    });

    const uploaded = await uploadFileDirect(
      `floor-plans/${Date.now()}-${compressedFile.name}`,
      compressedFile,
      `/floor-plans/upload`
    );

    return {
      url: uploaded.url,
      pathname: uploaded.pathname,
    };
  },

  // Dev Health (admin CI/test dashboard)
  getDevHealth: (): Promise<DevHealthResponse> => request('/dev-health'),

  // Field time submissions feed (admin/manager visibility)
  listTimeSubmissions: (): Promise<{ submissions: any[] }> => request('/time-submissions'),
};
